import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import '../index.css';

import { IncrementDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { Slider } from './Slider';
import { IncrementAmounts } from './IncrementAmounts';
import { PSBannerWrapper } from 'enrollment/components/Shared/Banner';
import { PsDrawerDialog } from 'stencilSetup';
import { selectPerPayPeriodText } from 'enrollment/services';
import {
  convertCurrency,
  getPremiumStyled,
  RETURN_DISPLAY,
  RETURN_VALUE_ONLY,
} from 'enrollment/utils';

type HTMLPsSelectFieldElement = HTMLSelectElement;
const formatAsCurrency = (amount?: string): string => {
  if (amount) {
    const numericAmount = parseFloat(amount);
    return isNaN(numericAmount)
      ? amount
      : numericAmount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          currencyDisplay: "symbol"
        });
  } else {
    return '';
  }
};

interface VolumeSelectProps {
  increments: IncrementDto[];
  onSliderChange: (newVolume: string) => void;
  selectedIncrement: IncrementDto | undefined;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  setSelectedIncrement: (increment: IncrementDto) => void;
  planName: string;
  planId: number;
  hideEmptyCost: boolean;
}

interface PerPayPeriodProps {
  cost: string;
  sliderValue: number;
  lastNonEoiVolume: number;
  lastNonEoiPPP: number;
  planId?: number;
}
export const PerPayPeriod = ({
  cost,
  sliderValue,
  lastNonEoiVolume,
  lastNonEoiPPP,
  planId,
}: PerPayPeriodProps) => {
  const { t } = useTranslation('pages.volume');

  const perPayText = useSelector(selectPerPayPeriodText);

  const additionalCost = getPremiumStyled({
    premium: (
      parseFloat(cost.replace(/[^0-9.]/g, '')) - lastNonEoiPPP
    ).toString(),
    valueOnly: RETURN_DISPLAY,
  });
  const premium = getPremiumStyled({
    premium: parseFloat(cost.replace(/[^0-9.]/g, '')).toString(),
    valueOnly: RETURN_DISPLAY,
  });

  const handleEOIinfo = () => {
    const infoDialog = document.querySelector(`#dialog_eoi_info_${planId}`);
    document.dispatchEvent(
      new CustomEvent('psOpenDialog', { detail: infoDialog })
    );
  };

  return (
    <>
      <div
        className={sliderValue > lastNonEoiVolume ? 'col-12 col-md-8' : 'col-4'}
      >
        <div className="row">
          <div className={sliderValue > lastNonEoiVolume ? 'col-3' : 'col'}>
            <div id="per-pay-period">
              <div id="cost">
                {getPremiumStyled({
                  premium: cost.replace(/[^0-9.]/g, ''),
                  valueOnly: RETURN_VALUE_ONLY,
                })}
              </div>
              {`${perPayText}`}
            </div>
          </div>
          {lastNonEoiPPP > 0 && sliderValue > lastNonEoiVolume && (
            <>
              <div className="col-2 my-auto eoi-calc">
                <span>
                  <i className="ph-bold ph-equals"></i>
                </span>
              </div>

              <div className="col-2 col-md col-lg-3 px-0 eoi-per-pay-period">
                <div id="initcost">
                  {getPremiumStyled({
                    premium: lastNonEoiPPP.toString(),
                    valueOnly: RETURN_VALUE_ONLY,
                  })}
                </div>
                <p className="body2">
                  {t('guaranteedAmt')}{' '}
                  {getPremiumStyled({
                    premium: lastNonEoiVolume.toString(),
                    valueOnly: RETURN_VALUE_ONLY,
                  })}
                </p>
              </div>

              <div className="col-4 col-md col-lg-4 p-0">
                <div className="d-flex">
                  <span className="eoi-calc my-sm-auto pr-3">
                    <i className="ph-bold ph-plus"></i>
                  </span>
                  <div className="mx-auto">
                    <div className="eoi-per-pay-period">
                      <div id="additionalcost">
                        {getPremiumStyled({
                          premium: (
                            parseFloat(cost.replace(/[^0-9.]/g, '')) -
                            lastNonEoiPPP
                          ).toString(),
                          valueOnly: RETURN_VALUE_ONLY,
                        })}
                      </div>
                      <p className="body2">
                        {t('additionalAmt')}{' '}
                        {getPremiumStyled({
                          premium: String(sliderValue - lastNonEoiVolume),
                          valueOnly: RETURN_VALUE_ONLY,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {sliderValue > lastNonEoiVolume && (
        <>
          <div className="container-fluid mt-0 ">
            <div
              className={`row mt-2 mr-sm-n2 justify-content-center ${lastNonEoiPPP > 0 ? 'justify-content-md-end' : 'justify-content-md-start'}`}
            >
              <div className="col-lg-4 d-none d-lg-block"></div>
              <div className="col-auto pt-1 px-0">
                <div id="needs-eoi-title" data-testid="eoi-pill">
                  <p className="overline1 mb-0">{t('EOITitle')}</p>
                </div>
              </div>
              <div className="col-auto d-md-none px-0">
                <ps-button
                  id="close-icon-button"
                  type="button"
                  is-icon={true}
                  icon="info"
                  label="Needs Evidence of Insurability Approval"
                  level="tertiary"
                  extra-class="blackIcon mr-lg-3"
                  onClick={handleEOIinfo}
                />
              </div>
            </div>
          </div>
          <PsDrawerDialog
            dialogId={`dialog_eoi_info_${planId}`}
            closeButtonLabel="Close"
          >
            <div>
              <div className="per-pay-period-info">
                <div className="cost-info">{premium}</div>
                {`${perPayText}`}
              </div>
              <div className="row">
                <div className="col-5">
                  <div className="initcost">
                    {getPremiumStyled({
                      premium: lastNonEoiPPP.toString(),
                      valueOnly: RETURN_DISPLAY,
                    })}
                  </div>
                  <p className="label">
                    {t('guaranteedAmt')}
                    {convertCurrency(String(lastNonEoiVolume))}
                  </p>
                </div>
                <div className="col mx-auto">
                  <div>
                    <div className="additionalcost">{additionalCost}</div>
                    <p className="label">
                      {t('additionalAmt')}
                      {convertCurrency(String(sliderValue - lastNonEoiVolume))}
                    </p>
                  </div>
                  <div
                    className="needs-eoi-title d-inline-block"
                    data-testid="eoi-pill"
                  >
                    <p className="overline1 mb-0">{t('EOITitle')}</p>
                  </div>
                </div>
              </div>
            </div>
          </PsDrawerDialog>
        </>
      )}
    </>
  );
};

export const VolumeSelect = ({
  increments,
  onSliderChange,
  selectedIncrement,
  sliderValue,
  setSliderValue,
  setSelectedIncrement,
  planName,
  planId,
  hideEmptyCost,
}: VolumeSelectProps) => {
  const selectRef = useRef<HTMLPsSelectFieldElement>(null);
  const [showCoverageNumbers, setShowCoverageNumbers] = useState(false);
  const [cost, setCost] = useState('$0.00');
  const [maxVolume, setMaxVolume] = useState(0);
  const [lastNonEoiVolume, setLastNonEoiVolume] = useState(0);
  const [lastNonEoiPPP, setLastNonEoiPPP] = useState(0);
  const updateSliderValue = (value: number) => {
    setSliderValue(value);
  };

  useEffect(() => {
    // Format the selectedAmount before updating the select element
    const formattedAmount = formatAsCurrency(selectedIncrement?.volume);
    // Update the ps-select-field when selectedAmount changes
    const selectElement =
      selectRef.current && selectRef.current.querySelector('select');
    if (selectElement) {
      selectElement.innerHTML = `<option value="${selectedIncrement?.volume}">${formattedAmount}</option>`;
    }
  }, [selectedIncrement]);

  useEffect(() => {
    if (increments?.length > 0) {
      const maxVolumeValue = parseFloat(
        increments[increments.length - 1].volume
      );
      setMaxVolume(maxVolumeValue);

      // Find the last increment where requires_eoi is false
      const lastNonEoiIncrement = increments
        .filter((inc) => !inc.requires_eoi) // Filter out increments that require EOI
        .pop(); // Get the last element

      // Check if such an element exists and set it to state
      if (lastNonEoiIncrement) {
        setLastNonEoiVolume(parseFloat(lastNonEoiIncrement.volume));
        setLastNonEoiPPP(parseFloat(lastNonEoiIncrement.subscriber_premium));
      }
    }
  }, [increments]);

  // Use effect to update state when selectedIncrement changes
  useEffect(() => {
    if (selectedIncrement) {
      setSelectedIncrement(selectedIncrement);
      setCost(`$${selectedIncrement.subscriber_premium}`);
    } else {
      setCost('$0.00');
    }
  }, [selectedIncrement, setSelectedIncrement]);

  interface SliderWithMaxProps {
    sliderValue: number;
    maxVolume: number;
    lastNonEoiVolume: number;
  }
  const SliderWithMax: React.FC<
    SliderWithMaxProps & { onSliderChange: (newVolume: string) => void }
  > = ({ sliderValue, maxVolume, lastNonEoiVolume, onSliderChange }) => {
    const minValue = 0;
    // Convert the maxVolume to string for Slider component if necessary
    return (
      <div
        className="mt-5 d-none d-md-block"
        data-testid="volume-select-slider"
      >
        <Slider
          id="coverage-slider"
          min={minValue}
          max={maxVolume}
          value={sliderValue}
          lastNonEoiVolume={lastNonEoiVolume}
          maxSliderValue={maxVolume}
          increments={increments}
          onValueChange={onSliderChange}
        />
        {sliderValue > lastNonEoiVolume && (
          <PSBannerWrapper
            id="eoi-notice"
            data-testid="eoi-notice"
            message={t('eoiNotice')}
            level="info"
            icon={true}
            extraClass="mb-4"
          />
        )}
      </div>
    );
  };

  const handleSelectClick = (e: React.MouseEvent<HTMLSelectElement>) => {
    // Toggle the coverage numbers visibility
    e.preventDefault();
    setShowCoverageNumbers(!showCoverageNumbers);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (['Enter', ' ', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      setShowCoverageNumbers((prev) => !prev);
    }
  };

  const handleAmountChange = (increment?: IncrementDto) => {
    if (increment) {
      setSelectedIncrement(increment); // Update state with the new amount

      // Directly update the web component's select element
      const selectElement =
        selectRef.current && selectRef.current.querySelector('select');
      if (selectElement) {
        const formattedAmount = formatAsCurrency(String(increment?.volume));
        selectElement.innerHTML = `<option value="${increment?.volume}">${formattedAmount}</option>`;
      }
      setShowCoverageNumbers(false);
    }
  };

  const { t } = useTranslation('pages.volume');

  const selectedAmount = selectedIncrement?.volume || 'Select Amount';

  return (
    <div className="px-4 px-md-5">
      <div className="row d-md-none">
        <div className="col-4 my-2">
          <div className="headline-2 text-center">{planName}</div>
        </div>
      </div>
      <div className="row pb-4_5 pt-2">
        <div className="col-4 order-2 order-md-first order-lg-first col-md-5 col-lg-4">
          <ps-select-field
            ref={selectRef}
            input-id="coverage-amount"
            label={t('coverageAmt')}
            onMouseDown={handleSelectClick}
            onKeyDown={handleKeyPress}
          >
            <option value={selectedAmount}>
              {formatAsCurrency(selectedAmount.replace(/[^0-9.]/g, ''))}
            </option>
          </ps-select-field>
          <IncrementAmounts
            show={showCoverageNumbers}
            maxValue={maxVolume}
            lastNonEoiVolume={lastNonEoiVolume}
            onAmountChange={handleAmountChange}
            setCost={setCost}
            increments={increments}
            onButtonClick={updateSliderValue}
            setShowCoverageNumbers={setShowCoverageNumbers}
            selectRef={selectRef}
            hideEmptyCost={hideEmptyCost}
          />
        </div>
        {(!hideEmptyCost || Number(cost) > 0) && (
          <PerPayPeriod
            cost={cost}
            sliderValue={sliderValue}
            lastNonEoiVolume={lastNonEoiVolume}
            lastNonEoiPPP={lastNonEoiPPP}
            planId={planId}
          />
        )}
      </div>
      <SliderWithMax
        sliderValue={sliderValue}
        maxVolume={maxVolume}
        lastNonEoiVolume={lastNonEoiVolume}
        onSliderChange={onSliderChange} // Pass it to SliderWithMax
      />
    </div>
  );
};
